import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../services/api';
import type { GenerateRequest, GenerateResponse } from '../types';

// ---- Query Keys ----
// Centralized key factory prevents stale/conflicting cache entries.
export const queryKeys = {
  generationStatus: (sessionId: string) => ['generation', 'status', sessionId] as const,
  session: (sessionId: string) => ['generation', 'session', sessionId] as const,
  generationsList: () => ['generations', 'list'] as const,
};

// ---- Status Polling ----
// Replaces BOTH the hand-rolled while-loop in api.generate() and the
// setTimeout-based polling in GenerationContext.  TanStack Query's
// refetchInterval gives us a single, declarative polling strategy that
// automatically stops when the component unmounts or when we return false.

export interface GenerationStatusResponse {
  status: string;
  progress?: number;
  message?: string;
  error?: string;
}

export function useGenerationStatus(
  sessionId: string | null,
  options?: {
    /** Whether polling is currently active. Defaults to true when sessionId is set. */
    enabled?: boolean;
    /** Polling interval in ms. Defaults to 2000. */
    pollingInterval?: number;
    /** Called when status reaches 'complete' or 'failed'. */
    onSettled?: (status: GenerationStatusResponse) => void;
  }
) {
  const enabled = options?.enabled ?? !!sessionId;
  const pollingInterval = options?.pollingInterval ?? 2000;

  return useQuery<GenerationStatusResponse>({
    queryKey: queryKeys.generationStatus(sessionId ?? ''),
    queryFn: async ({ signal }) => {
      if (!sessionId) throw new Error('No session ID');
      return api.getGenerationStatus(sessionId, signal);
    },
    enabled: enabled && !!sessionId,
    // Polling: return interval ms to keep polling, false to stop.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return pollingInterval;
      // Stop polling once terminal
      if (data.status === 'complete' || data.status === 'failed') {
        return false;
      }
      return pollingInterval;
    },
    // Keep stale data visible while refetching
    staleTime: 0,
    // Disable automatic garbage-collection while polling
    gcTime: Infinity,
    // Notify on every poll so subscribers always get the latest
    notifyOnChangeProps: 'all',
  });
}

// ---- Session Data (full result after generation completes) ----

export function useSession(sessionId: string | null | undefined) {
  return useQuery<GenerateResponse>({
    queryKey: queryKeys.session(sessionId ?? ''),
    queryFn: async ({ signal }) => {
      if (!sessionId) throw new Error('No session ID');
      return api.getSession(sessionId, signal);
    },
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // Session data rarely changes - cache 5 min
  });
}

// ---- Generations List ----

export interface GenerationListItem {
  sessionId: string;
  companyName: string;
  status: 'pending' | 'generating' | 'complete' | 'failed';
  progress: number;
  message: string;
  createdAt: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export function useGenerationsList(options?: { enabled?: boolean; page?: number; limit?: number }) {
  const page = options?.page;
  const limit = options?.limit;

  return useQuery<{ sessions: GenerationListItem[]; pagination: PaginationMeta }>({
    queryKey: [...queryKeys.generationsList(), { page, limit }] as const,
    queryFn: ({ signal }) => api.listGenerations({ page, limit, signal }),
    enabled: options?.enabled ?? true,
    staleTime: 10_000,
  });
}

// ---- Start Generation Mutation ----

export function useStartGenerationMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    { sessionId: string; status: string },
    ApiError,
    GenerateRequest
  >({
    mutationFn: (data: GenerateRequest) => api.startGeneration(data),
    onSuccess: () => {
      // Invalidate the generations list so it refetches
      queryClient.invalidateQueries({ queryKey: queryKeys.generationsList() });
    },
  });
}

// ---- Cancel Generation Mutation ----

export function useCancelGenerationMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: (sessionId: string) => api.cancelGeneration(sessionId),
    onSuccess: (_data, sessionId) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: queryKeys.generationStatus(sessionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.generationsList() });
    },
  });
}

// ---- Delete Generation Mutation ----

export function useDeleteGenerationMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: (sessionId: string) => api.deleteGeneration(sessionId),
    onSuccess: (_data, sessionId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.session(sessionId) });
      queryClient.removeQueries({ queryKey: queryKeys.generationStatus(sessionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.generationsList() });
    },
  });
}
